import { get } from 'lodash-es';
import { getLocale } from 'umi';
import type {
  PrMetricSeriesConfig,
  PrMetricEventData,
  PrMetricBubbleEventItem,
  PrMetricChartEventSetting,
} from './components/PrMetricTtrend/types';
import type {
  IBuildPrDownloadPayloadOptions,
  IDownloadColumn,
  IPrGameChannelItem,
  IPrMentionTimelineSource,
  IPrTimelineDayBlock,
  IPrTrendSource,
  IPrStudioDataNode,
  PrEffectiveSortResult,
  PrMultiSortFirst,
  PrStudioListTabKey,
  PrSummaryCardMetricConfig,
  PrTrendTrimOptions,
  PrTrendTrimReturnShape,
  TPrTrimSeriesItem,
} from './_type';
export type {
  IBuildPrDownloadPayloadOptions,
  IPrGameChannelItem,
  PrStudioListTabKey,
  PrSummaryCardMetricConfig,
} from './_type';

type IFormatPrTrendForChartOptions = {
  seriesName?: string;
};

/**
 * 路由段 prHubGameCode 与 getGameRouteCode 一致：可能是 game_code，也可能是 unified_edition_id。
 * games 接口项只带 unified_edition_id，匹配前需先解析为统一 ID（需 data.studioData.game_list）。
 */
export function resolvePrHubUnifiedEditionId(data: Record<string, unknown>): string | undefined {
  const raw = String(data?.prHubGameCode ?? '').trim();
  if (!raw) {
    return undefined;
  }
  const studioDataTyped = data?.studioData as IPrStudioDataNode | undefined;
  const gameList = studioDataTyped?.game_list;
  if (!gameList?.length) {
    return raw;
  }
  if (gameList.some((g) => g.unified_edition_id === raw)) {
    return raw;
  }
  const byCode = gameList.find((g) => g.game_code && g.game_code === raw);
  if (byCode?.unified_edition_id) {
    return byCode.unified_edition_id;
  }
  return raw;
}

/**
 * 当前 PR Hub 选中游戏的展示名称（与 KPI 区逻辑一致，来自 getPrGamesWithChannels 的 game_name）。
 * `data.prHubGameCode` 建议已与路由 game_code / props 对齐后再传入。
 */
export function resolvePrCurrentGameDisplayName(
  data: Record<string, unknown> | undefined,
  prGamesChannels: IPrGameChannelItem[] | undefined,
): string {
  if (!prGamesChannels?.length) {
    return '';
  }
  const unifiedId = resolvePrHubUnifiedEditionId(data || {}) || '';
  if (!unifiedId) {
    return '';
  }
  const matched = prGamesChannels.find((g) => g.unified_edition_id === unifiedId);
  const name = matched?.game_name;
  return typeof name === 'string' ? name.trim() : '';
}

function prFilterDateDigitsYyyymmdd(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return '';
  const digits = String(raw).replace(/\D/g, '');
  return digits.length >= 8 ? digits.slice(0, 8) : digits;
}

function todayYyyymmdd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${mo}${day}`;
}

/** `\ / : * ? " < > |` → `_`，避免出现非法下载文件名字符；保留中英文及空格（与产品示例「Dune Awakening」一致） */
function sanitizePrDownloadBasenameSegment(s: string, fallback: string): string {
  const t = String(s || '').trim();
  const raw = t ? t.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim() : '';
  return raw || fallback;
}

/**
 * `prDownloadTab`（与下载接口 `tab` 一致）→ 文件名中的「当前指标」段。
 * @see PR 表格下载：`PR_{游戏}_{指标}_{开始}_{结束}`
 */
export function getPrExcelDownloadMetricSegment(prDownloadTab: string): string {
  const key = String(prDownloadTab || '').trim();
  const map: Record<string, string> = {
    media: 'Media',
    regions: 'Regions',
    articles: 'Articles',
    posts: 'Posts',
    videos: 'Videos',
    streams: 'Streams',
    overviewArticles: 'Overview Articles',
  };
  return map[key] || key;
}

/**
 * PR 表格/趋势图下载文件名**基名**（不含扩展名）：
 * `PR_{game}_{segment}_{startYYYYMMDD}_{endYYYYMMDD}`，与 PR Hub 文案规范对齐。
 */
export function buildPrBackendDownloadExcelBasename(
  data: Record<string, unknown>,
  metricSegment: string,
): string {
  const gameRaw =
    resolvePrCurrentGameDisplayName(data, data?.prGamesChannels as IPrGameChannelItem[] | undefined) ||
    'Game';
  const game = sanitizePrDownloadBasenameSegment(gameRaw, 'Game');
  const segment = sanitizePrDownloadBasenameSegment(metricSegment, 'Export');
  const fv = (data?.filterVal ?? data?.filter) as Record<string, unknown> | undefined;
  let start = prFilterDateDigitsYyyymmdd(fv?.start_date);
  let end = prFilterDateDigitsYyyymmdd(fv?.end_date ?? fv?.start_date);
  if (!start) start = todayYyyymmdd();
  if (!end) end = start;
  return `PR_${game}_${segment}_${start}_${end}`;
}

/** 后端表格导出：接口返回 CSV，文件名需为 `.csv`（与 Content-Disposition 一致，避免被误存为 .xlsx）。 */
export function buildPrBackendDownloadCsvFileName(
  data: Record<string, unknown>,
  metricSegment: string,
): string {
  return `${buildPrBackendDownloadExcelBasename(data, metricSegment)}.csv`;
}

/** 从 prGamesChannels 按当前 Hub 游戏解析 brand_id（已处理 game_code / unified_edition_id） */
function resolvePrBrandId(data: Record<string, unknown>): number | undefined {
  const prGamesChannels = data?.prGamesChannels as IPrGameChannelItem[] | undefined;
  const unifiedId = resolvePrHubUnifiedEditionId(data);
  if (!prGamesChannels?.length || !unifiedId) {
    return undefined;
  }
  const matched = prGamesChannels.find((g) => g.unified_edition_id === unifiedId);
  return matched?.brand_id;
}

/**
 * PR 下载接口：列表与表头 `sort_by` 使用短名，下载 body 的列 key / filter.sort_by 需带 `_total` 后缀。
 * 非指标列（如 media、region）无映射时原样透传。
 */
const PR_DOWNLOAD_LIST_CODE_TO_API_KEY: Readonly<Record<string, string>> = {
  // Media
  audience: 'audience_total',
  followers: 'followers_total',
  publications: 'publications_total',
  engagement: 'engagement_total',
  // Regions
  creators: 'creators_total',
  views: 'views_total',
  hours_watched: 'hours_watched_total',
};

function mapPrDownloadMetricFieldKey(key: string): string {
  return PR_DOWNLOAD_LIST_CODE_TO_API_KEY[key] ?? key;
}

/** 下载 body 的 `language` 及 Regions 地区列用：优先显式入参，否则跟站点语言（en-US / 其他 → zh） */
export function resolvePrDownloadLanguage(explicit?: 'en' | 'zh'): 'en' | 'zh' {
  if (explicit === 'en' || explicit === 'zh') {
    return explicit;
  }
  return getLocale() === 'en-US' ? 'en' : 'zh';
}

/**
 * 当前表格真实排序。CommonTable 后端分页时用户点表头会更新 `extraParams.multi_sort`，
 * 根上的 `sort_by` / `desc` 可能仍是 `buildPrListExtraParams` 的初始值，需优先读 `multi_sort[0]`。
 */
function getPrEffectiveSort(extraParams: Record<string, unknown>): PrEffectiveSortResult {
  const multi = extraParams?.multi_sort;
  if (Array.isArray(multi) && multi.length > 0) {
    const first = multi[0] as PrMultiSortFirst;
    if (
      first &&
      typeof first === 'object' &&
      first.sort_by !== undefined &&
      first.sort_by !== null &&
      String(first.sort_by).trim() !== ''
    ) {
      return {
        sort_by: typeof first.sort_by === 'string' ? first.sort_by : String(first.sort_by),
        desc: Boolean(first.desc),
      };
    }
  }
  const sortBy = extraParams?.sort_by ?? '';
  const desc = extraParams?.desc ?? true;
  return {
    sort_by: typeof sortBy === 'string' ? sortBy : String(sortBy ?? ''),
    desc: Boolean(desc),
  };
}

/**
 * PR 下载接口专用请求体构建（对齐 /api/v2/dashboard/studio/pr/download）。
 *
 * 与 buildPrStudioPayload 的区别：
 * - `language` 固定为英文（表头始终以英文导出）
 * - sort_by / desc / keyword 嵌套到 filter 对象
 * - 增加 columns 数组（从表格列配置转换）
 * - 不传 page / page_size（下载接口不需要分页）
 */
export function buildPrDownloadPayload(
  data: Record<string, unknown>,
  options: IBuildPrDownloadPayloadOptions,
): Record<string, unknown> {
  const { tab, tableColumns = [] } = options;
  /** 表格下载：表头固定英文（与 PR Hub 导出规范一致） */
  const downloadLang: 'en' = 'en';

  // 基础字段复用 buildPrStudioPayload 逻辑
  const extraParams = (data?.extraParams as Record<string, unknown>) || {};
  const fv = (data?.filterVal ?? data?.filter) as Record<string, unknown> | null | undefined;
  const fromFilter: Record<string, unknown> = {};

  if (fv && typeof fv === 'object') {
    if (fv.start_date !== undefined && fv.start_date !== null && fv.start_date !== '') {
      fromFilter.start_date = fv.start_date;
    }
    if (fv.end_date !== undefined && fv.end_date !== null && fv.end_date !== '') {
      fromFilter.end_date = fv.end_date;
    }
  }

  const brandId = resolvePrBrandId(data);

  // 与表格一致：以 multi_sort 为准（表头排序后只更新该项）
  const { sort_by: sortBy, desc } = getPrEffectiveSort(extraParams);
  const mapDownloadKeys = tab === 'regions' || tab === 'media';
  const downloadSortBy = mapDownloadKeys ? mapPrDownloadMetricFieldKey(sortBy) : sortBy;
  const keyword = (extraParams as Record<string, unknown>).keyword ?? '';

  /** Regions：英文表头固定为 Region，列 key 使用 region_en */
  const tableColumnsResolved =
    tab === 'regions'
      ? [
        { code: 'region_en', nameEn: 'Region', nameCn: 'Region' },
        ...tableColumns.filter(
          (c) => c.code && !['region', 'region_en', 'region_zh'].includes(String(c.code)),
        ),
      ]
      : tableColumns;

  // 将 tableColumns 转换为 columns 数组
  const columns: IDownloadColumn[] = tableColumnsResolved
    .filter((col) => col.code)
    .map((col) => ({
      key: mapDownloadKeys ? mapPrDownloadMetricFieldKey(col.code!) : col.code!,
      label_en: col.nameEn || col.name || '',
      label_zh: col.nameCn || col.name || '',
    }));

  return {
    studio_code: data?.frameCode,
    ...(brandId !== undefined ? { brand_id: brandId } : {}),
    ...fromFilter,
    tab,
    // 和产品核对后固定下载英文
    language: downloadLang,
    filter: {
      sort_by: downloadSortBy,
      desc,
      keyword: typeof keyword === 'string' ? keyword : String(keyword ?? ''),
    },
    columns,
  };
}

/**
 * PR 各 Tab 列表默认排序：与列 `code` 及接口 `sort_by` 一致（可只改此处统一调整）。
 * `buildPrListExtraParams` 会写入 `sort_by` / `desc`，并附带 `multi_sort` 供 CommonTable 表头展示初始排序（不发给后端，见 buildPrStudioPayload）。
 */
export const PR_STUDIO_TABLE_DEFAULT_SORT = {
  media: { sort_by: 'publications', desc: true },
  regions: { sort_by: 'publications', desc: true },
  posts: { sort_by: 'engagement', desc: true },
  videos: { sort_by: 'engagement', desc: true },
  streams: { sort_by: 'max_cc_viewers', desc: true },
  articles: { sort_by: 'date', desc: true },
} as const;

/** 列表 Service 的 `data.extraParams`：分页 + 默认排序 + 表头 `multi_sort` */
export function buildPrListExtraParams(
  tab: PrStudioListTabKey,
  pageSize = 10,
): Record<string, unknown> {
  const s = PR_STUDIO_TABLE_DEFAULT_SORT[tab];
  return {
    page: 1,
    page_size: pageSize,
    sort_by: s.sort_by,
    desc: s.desc,
    multi_sort: [{ sort_by: s.sort_by, desc: s.desc }],
  };
}

/**
 * Studio PR 请求体构建（对齐 level-up-api.md）。
 *
 * 通用参数：studio_code / brand_id / start_date / end_date
 * 列表接口追加：page / page_size / sort_by / desc / keyword
 * Trend 接口追加：time_granularity
 */
export function buildPrStudioPayload(data: Record<string, unknown>, extra: Record<string, unknown> = {}) {
  const extraParamsRaw = (data?.extraParams as Record<string, unknown>) || {};
  const restExtra: Record<string, unknown> = { ...extraParamsRaw };
  delete restExtra.multi_sort;
  const effectiveSort = getPrEffectiveSort(extraParamsRaw);
  const extraParams = { ...restExtra, sort_by: effectiveSort.sort_by, desc: effectiveSort.desc };
  const fv = (data?.filterVal ?? data?.filter) as Record<string, unknown> | null | undefined;
  const fromFilter: Record<string, unknown> = {};

  if (fv && typeof fv === 'object') {
    // start_date / end_date (YYYY-MM-DD)
    if (fv.start_date !== undefined && fv.start_date !== null && fv.start_date !== '') {
      fromFilter.start_date = fv.start_date;
    }
    if (fv.end_date !== undefined && fv.end_date !== null && fv.end_date !== '') {
      fromFilter.end_date = fv.end_date;
    }
    // time_granularity (daily / monthly)
    if (fv.time_granularity !== undefined && fv.time_granularity !== null && fv.time_granularity !== '') {
      fromFilter.time_granularity = fv.time_granularity;
    }
  }

  const brandId = resolvePrBrandId(data);

  return {
    studio_code: data?.frameCode,
    ...(brandId !== undefined ? { brand_id: brandId } : {}),
    ...fromFilter,
    ...extra,
    ...extraParams,
  };
}

/**
 * 趋势图（overview/trend）专用请求参数构建。
 * 默认状态下自动扩大请求范围以获取足够数据：
 * - 月粒度 → 请求最近 7 个月
 * - 日粒度 → 请求最近 15 天
 * 用户手动改过日期后（isDateChanged=true），按筛选器实际选择的时间范围请求。
 */

/** 将 Date 格式化为本地时区 YYYY-MM-DD（避免 toISOString 的 UTC 偏移问题） */
function fmtLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export function buildPrTrendPayload(data: Record<string, unknown>, extra: Record<string, unknown> = {}) {
  const fv = (data?.filterVal ?? data?.filter) as Record<string, unknown> | null | undefined;
  const isDateChanged = Boolean(data?.isDateChanged);

  /** 默认状态：扩展 start_date 以获取更多历史数据 */
  if (!isDateChanged && fv && typeof fv === 'object') {
    const granularity = String(fv.time_granularity || '');
    const endDate = String(fv.end_date || '');
    if (endDate) {
      let expandedStart: string;
      if (granularity === 'monthly') {
        // 月粒度：往前推 7 个月，start 取月初1号，end 取月末最后一天
        const endD = new Date(`${endDate}T00:00:00`);
        // 确保 end 是该月最后一天
        const endOfMonth = fmtLocalDate(new Date(endD.getFullYear(), endD.getMonth() + 1, 0));
        // start 往前推 6 个月，取该月1号
        expandedStart = fmtLocalDate(new Date(endD.getFullYear(), endD.getMonth() - 6, 1));
        return buildPrStudioPayload(
          { ...data, filterVal: { ...fv as Record<string, unknown>, start_date: expandedStart, end_date: endOfMonth } },
          extra,
        );
      }
      // 日粒度：往前推 15 天（含当天）
      {
        const d = new Date(`${endDate}T00:00:00`);
        d.setDate(d.getDate() - 14);
        expandedStart = fmtLocalDate(d);
        return buildPrStudioPayload(
          { ...data, filterVal: { ...fv as Record<string, unknown>, start_date: expandedStart, end_date: endDate } },
          extra,
        );
      }
    }
  }

  return buildPrStudioPayload(data, extra);
}

/**
 * PR 2.3 核心指标卡片配置项（与 pageConfig 中 PR_SUMMARY_CARDS_METRICS 一致，定义见需求 2.3.1）
 */
/**
 * 格式化 Overview KPI 卡片返回
 * API 返回格式：{ code, data: { media: {...}, articles: {...}, posts: {...}, est_impressions: {...} }, req }
 * @param metrics 卡片顺序、标题与 formatRule 由页面配置传入
 */
export function formatPrSummaryCardsResponse(
  res: unknown,
  metrics: PrSummaryCardMetricConfig[],
) {
  if (!Array.isArray(metrics) || metrics.length === 0) {
    return { items: [] };
  }
  const resObj =
    res && typeof res === 'object' && !Array.isArray(res)
      ? (res as Record<string, unknown>)
      : {};
  const srcMaybe = (resObj.data ?? resObj);
  const src =
    srcMaybe && typeof srcMaybe === 'object' && !Array.isArray(srcMaybe)
      ? (srcMaybe as Record<string, unknown>)
      : {};
  const items = metrics.map((def) => {
    const block = (src as Record<string, unknown>)[def.metricKey];
    const patchRaw =
      block && typeof block === 'object' && !Array.isArray(block)
        ? (block as Record<string, unknown>)
        : {};
    /** 块内字段统一解包为标量，避免 dynamicString / 副文案取到 { total, by_channel } */
    const patch: Record<string, unknown> = {};
    Object.keys(patchRaw).forEach((k) => {
      patch[k] = coercePrMetricScalar(patchRaw[k]);
    });
    const value = def.valueKey ? patch[def.valueKey] : undefined;
    const tooltipEn = def.tooltipEn || '';
    const tooltipCn = def.tooltipCn || '';
    const next: Record<string, unknown> = {
      metricKey: def.metricKey,
      title_en: def.enName,
      title_cn: def.cnName,
      tooltip_en: tooltipEn,
      tooltip_cn: tooltipCn,
      value,
      formatRule: def.formatRule,
    };
    if (def.subFormatRule) {
      next.subFormatRule = def.subFormatRule;
    }
    /**
     * 副文案模板由 PrSummaryCards 内用 dynamicString 渲染（避免纯配置/远端 formatData 内嵌复杂表达式）
     */
    if (def.subTemplateEn || def.subTemplateCn) {
      next.subTemplateEn = def.subTemplateEn;
      next.subTemplateCn = def.subTemplateCn;
      next.subTemplatePatch = patch;
    } else if (def.subValueKey) {
      /** 无模板时，降级为纯数值展示 */
      const subValue = patch[def.subValueKey];
      if (subValue !== undefined && subValue !== null) {
        const text =
          typeof subValue === 'object' && !Array.isArray(subValue) ? '-' : String(subValue);
        next.sub_en = text;
        next.sub_cn = text;
      }
    }
    return next;
  });
  return { items };
}

/**
 * 裁剪趋势图数据到默认展示窗口
 * - isDateChanged=false（默认状态）时生效：
 *   - 月粒度：最近 7 个月（含当前月）
 *   - 日粒度：最近 15 天（含 T-0）
 * - isDateChanged=true（用户手动改过日期）时，原样返回不裁剪
 */
export function trimPrTrendDefaultWindow(
  categories: string[],
  series: TPrTrimSeriesItem[],
  options?: PrTrendTrimOptions,
): PrTrendTrimReturnShape {
  if (!categories.length || !series.length) return { categories, series };

  /** 用户手动改过日期 → 不裁剪 */
  if (options?.isDateChanged) return { categories, series };

  const isMonthly = options?.timeGranularity === 'monthly';
  const maxItems = isMonthly ? 7 : 15;

  if (categories.length <= maxItems) return { categories, series };

  const slicedCategories = categories.slice(-maxItems);
  const slicedSeries = series.map((s) => ({
    ...s,
    data: s.data.slice(-maxItems),
  }));

  return { categories: slicedCategories, series: slicedSeries };
}

/**
 * API 返回格式：{ code, data: { time_granularity, field, points: [{ bucket, value }] }, req }
 */
export function formatPrTrendForChart(
  res: unknown,
  options?: IFormatPrTrendForChartOptions,
) {
  const src = ((res as { data?: unknown; })?.data ?? res) as IPrTrendSource;
  const points = Array.isArray(src?.points) ? src.points : [];
  const categories = points.map((p) => String(p?.bucket ?? ''));
  const values = points.map((p) => {
    const v = p?.value;
    return v === '' || v === null || v === undefined ? 0 : Number(v);
  });

  const fallbackSeriesName = String(src?.field ?? 'articles').replace(
    /^([a-z])/,
    (s) => s.toUpperCase(),
  );
  const configuredSeriesName = String(options?.seriesName ?? '').trim();

  const series: PrMetricSeriesConfig[] = [
    {
      name: configuredSeriesName || fallbackSeriesName,
      data: values,
      color: undefined,
    },
  ];

  return {
    categories,
    series,
  };
}

/**
 * 事件时间线接口专用入参构建（字段名对齐 /api/v1/opinion_pc/overview/mentions_stats_timeline）
 * 要求字段：start_time, end_time (YYYY-MM-DD HH:mm:ss), edition_unified_id
 */
export function buildPrEventTimelinePayload(data: Record<string, unknown>) {
  const fv = (data?.filterVal ?? data?.filter) as Record<string, unknown> | null | undefined;
  const fromFilter: Record<string, unknown> = {};

  if (fv && typeof fv === 'object') {
    if (fv.start_date !== undefined && fv.start_date !== null && fv.start_date !== '') {
      fromFilter.start_time = `${fv.start_date} 00:00:00`;
    }
    if (fv.end_date !== undefined && fv.end_date !== null && fv.end_date !== '') {
      fromFilter.end_time = `${fv.end_date} 23:59:59`;
    }
  }

  const editionUnifiedId = resolvePrHubUnifiedEditionId(data);

  // date_type：与趋势接口 time_granularity 对齐
  const dateType = (fv?.time_granularity === 'monthly' ? 'monthly' : 'daily') as string;

  return {
    ...fromFilter,
    entity_type: 'pc',
    id_type: 'edition_id',
    need_event_data: true,
    date_type: dateType,
    ...(editionUnifiedId ? { edition_unified_id: editionUnifiedId } : {}),
  };
}

/**
 * 格式化事件时间线接口返回，转换为 PrMetricTrendChart 所需的 eventData / chartEventSetting
 *
 * API 实际返回结构（来自 evenTestData）：
 * {
 *   data: {
 *     event_list: { "Announcement": [{ event_type, name, name_cn, start_time, end_time, ... }], "Version": [...] },
 *     event_timeline: {
 *       "2026-04-09 00:00:00": { count: 1, event_list: { "Announcement": [...] } },
 *       "2026-04-14 00:00:00": { count: 2, event_list: { "Version": [...], ... } }
 *     }
 *   }
 * }
 *
 * 注意：event_timeline 是 **对象**（日期 key），不是数组。
 * 每个日期下 event_list 按 event_type 分组，每组是完整事件项数组。
 *
 * @param filterVal - Service.formatData 第二参：当前筛选器的 time_granularity（接口 body 未带 granularity 时使用）
 */
export function formatPrMentionStatsTimeline(
  res: unknown,
  filterVal?: unknown,
): {
    eventData: PrMetricEventData;
    chartEventSetting: PrMetricChartEventSetting;
  } {
  const src = ((res as { data?: unknown; })?.data ?? res) as IPrMentionTimelineSource;
  const rawTimeline = src?.event_timeline && typeof src.event_timeline === 'object' && !Array.isArray(src.event_timeline)
    ? (src.event_timeline as Record<string, IPrTimelineDayBlock>)
    : {};

  const fv =
    filterVal && typeof filterVal === 'object' && !Array.isArray(filterVal)
      ? (filterVal as Record<string, unknown>)
      : {};
  const granFromFilter = fv.time_granularity;
  const granRaw = src?.time_granularity ?? src?.granularity ?? granFromFilter;
  const isMonthly = String(granRaw ?? '').toLowerCase() === 'monthly';

  /**
   * 月粒度：趋势图 bucket 为 YYYY-MM，事件接口 timeline key 常为 YYYY-MM-DD（月首），须与类目一致。
   * 日粒度：与原来一致，用 YYYY-MM-DD。
   */
  const timelineKeyToAxisKey = (rawDateKey: string): string => {
    const head = String(rawDateKey).trim().slice(0, 10);
    if (!head) return '';
    if (isMonthly) {
      const ym = head.slice(0, 7);
      return /^\d{4}-\d{2}$/.test(ym) ? ym : head;
    }
    return head;
  };

  /** bubbleEvent：按 X 轴类目聚合；日粒度 { '2026-04-09': [...] }，月粒度 { '2026-01': [...] } */
  const bubbleEvent: Record<string, PrMetricBubbleEventItem[]> = {};
  const chartRenderEventList: PrMetricEventData['chartRenderEventList'] = {};

  const groupBubbleItemsByType = (
    items: PrMetricBubbleEventItem[],
  ): Record<string, PrMetricBubbleEventItem[]> => {
    const grouped: Record<string, PrMetricBubbleEventItem[]> = {};
    items.forEach((it) => {
      const key = (it.event_type !== undefined && it.event_type !== null && `${it.event_type}`.trim() !== '')
        ? String(it.event_type).trim()
        : 'Event';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(it);
    });
    return grouped;
  };

  for (const rawDateKey of Object.keys(rawTimeline)) {
    if (!rawDateKey || !rawTimeline[rawDateKey]) continue;

    const dayBlock = rawTimeline[rawDateKey];
    if (typeof dayBlock !== 'object') continue;

    const dateKeyDay = String(rawDateKey).slice(0, 10);
    const axisKey = timelineKeyToAxisKey(rawDateKey);
    if (!axisKey) continue;

    const flatItems: PrMetricBubbleEventItem[] = [];

    const dayEventMap: Record<string, unknown[]> | undefined = dayBlock.event_list;
    if (dayEventMap && typeof dayEventMap === 'object') {
      for (const eventType of Object.keys(dayEventMap)) {
        const arr = dayEventMap[eventType];
        if (!Array.isArray(arr)) continue;
        for (const item of arr) {
          if (!item || typeof item !== 'object') continue;
          const ext = item as Record<string, unknown>;
          const toText = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
          const typeCnRaw = [ext.event_type_cn, ext.type_name_cn].find(
            (s) => typeof s === 'string' && s.trim() !== '',
          );
          flatItems.push({
            id: toText(ext.url) || toText(ext.start_time),
            event_id: undefined,
            event_type: toText(ext.event_type) || eventType,
            event_type_cn: typeCnRaw !== undefined && typeCnRaw !== null ? String(typeCnRaw).trim() : undefined,
            event_title_en: toText(ext.name),
            name: toText(ext.name),
            name_cn: toText(ext.name_cn),
            description: toText(ext.description),
            description_cn: toText(ext.description_cn),
            start_time: toText(ext.start_time),
            end_time: toText(ext.end_time),
            event_bar_color: undefined,
          });
        }
      }
    }

    /** 月粒度常见：仅有 count + event_types，无 event_list；仍须在柱顶展示事件标签 */
    if (flatItems.length === 0) {
      const types = Array.isArray(dayBlock.event_types) ? dayBlock.event_types as string[] : [];
      if (types.length > 0) {
        types.forEach((et, idx) => {
          const st = isMonthly ? `${axisKey}-01` : `${dateKeyDay} 00:00:00`;
          flatItems.push({
            id: `${axisKey}-${et}-${idx}`,
            event_id: undefined,
            event_type: et,
            event_title_en: et,
            name: et,
            name_cn: et,
            description: '',
            description_cn: '',
            start_time: st,
            end_time: '',
            event_bar_color: undefined,
          });
        });
      } else if (typeof dayBlock.count === 'number' && dayBlock.count > 0) {
        const st = isMonthly ? `${axisKey}-01` : `${dateKeyDay} 00:00:00`;
        flatItems.push({
          id: `${axisKey}-count`,
          event_id: undefined,
          event_type: undefined,
          event_title_en: `${dayBlock.count} events`,
          name: `${dayBlock.count} events`,
          name_cn: `${dayBlock.count} events`,
          description: '',
          description_cn: '',
          start_time: st,
          end_time: '',
          event_bar_color: undefined,
        });
      }
    }

    if (flatItems.length > 0) {
      const merged = [...(bubbleEvent[axisKey] ?? []), ...flatItems];
      bubbleEvent[axisKey] = merged;
      const displayCount =
        typeof dayBlock.count === 'number' && dayBlock.count > 0
          ? dayBlock.count
          : merged.length;
      chartRenderEventList[axisKey] = {
        count: displayCount,
        event_list: groupBubbleItemsByType(merged),
      };
    }
  }

  const hasEvents = Object.keys(bubbleEvent).length > 0;

  const eventDataOut: PrMetricEventData = {
    format: isMonthly ? 'YYYY-MM' : 'YYYY-MM-DD',
    chartRenderEventList,
  };
  const chartEventSettingOut: PrMetricChartEventSetting = {
    bubbleEvent,
    showEventAxis: hasEvents,
    showEventBubbles: hasEvents,
  };

  return {
    eventData: eventDataOut,
    chartEventSetting: chartEventSettingOut,
  };
}

/**
 * 宽表 / KPI 指标常见结构：{ value } 或 { total, by_channel? }。
 * 表格与卡片只展示标量，避免把对象当作 React 子节点渲染。
 */
export function coercePrMetricScalar(input: unknown): unknown {
  if (input === null || input === undefined) {
    return input;
  }
  if (typeof input !== 'object' || Array.isArray(input)) {
    return input;
  }
  const o = input as Record<string, unknown>;
  if ('value' in o && o.value !== undefined) {
    return o.value;
  }
  if ('total' in o) {
    const t = o.total;
    if (typeof t === 'number' || typeof t === 'string' || t === null || t === undefined) {
      return t;
    }
  }
  return input;
}

/** 宽表行 metrics.{key}.value / {total, by_channel} → 扁平字段，供 CommonTable 列 code 直接取值 */
function flattenDashboardMetricRow(row: unknown): Record<string, unknown> {
  if (!row || typeof row !== 'object') {
    return {};
  }
  const r = row as Record<string, unknown>;
  const next: Record<string, unknown> = { ...r };

  /** 1) 处理 metrics 子对象（宽表旧格式） */
  const { metrics } = r;
  if (metrics && typeof metrics === 'object' && !Array.isArray(metrics)) {
    Object.keys(metrics as Record<string, unknown>).forEach((k) => {
      const m = (metrics as Record<string, unknown>)[k];
      const scalar = coercePrMetricScalar(m);
      if (scalar !== null && typeof scalar === 'object' && !Array.isArray(scalar)) {
        next[k] = '-';
      } else {
        next[k] = scalar;
      }
    });
  }

  /** 2) 处理 {total, by_channel} 结构的字段（PR 列表接口格式），提取 total 标量值 */
  for (const key of Object.keys(next)) {
    if (key === 'metrics') continue;
    const val = next[key];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const obj = val as Record<string, unknown>;
      if ('total' in obj && typeof obj.total !== 'object') {
        // 供 Media 等富单元格使用：保留分渠道数据（支持对象或数组格式）
        const byCh = obj.by_channel;
        if (byCh && typeof byCh === 'object') {
          // 数组 [{ code, value }] 或 对象 { channelName: value }
          next[`${key}__by_channel`] = byCh;
        }
        // { total: number/string, by_channel: ... } → 取 total
        next[key] = coercePrMetricScalar(obj.total);
      }
    }
  }

  return next;
}

/**
 * 将列表类接口结果规范为 CommonTable 可用的 metrics_list / row_count
 * API 返回格式：{ channels, rows, page, page_size, total }
 * 同时保留 channels 供表格动态列渲染使用
 */
export function normalizePrPressTableResponse(res: unknown) {
  const resObj =
    res && typeof res === 'object' && !Array.isArray(res)
      ? (res as Record<string, unknown>)
      : {};
  const rawList =
    get(res, 'metrics_list') ??
    get(res, 'data.metrics_list') ??
    get(res, 'rows') ??
    get(res, 'data.rows') ??
    [];
  const metricsList = Array.isArray(rawList) ? rawList.map(flattenDashboardMetricRow) : [];
  const rowCount =
    get(res, 'row_count') ??
    get(res, 'data.row_count') ??
    get(res, 'total') ??
    get(res, 'data.total') ??
    metricsList.length;
  const channels =
    get(res, 'channels') ??
    get(res, 'data.channels') ??
    [];

  return {
    ...resObj,
    metrics_list: metricsList,
    row_count: typeof rowCount === 'number' ? rowCount : Number(rowCount) || 0,
    channels: Array.isArray(channels) ? channels : [],
  };
}
